/* Production & Manufacturing Network — a faithful React port of the prototype's
 * rProduction screen. Plan each quarter's build volumes against network capacity,
 * route every generation's order across factories + outsource partners, build /
 * expand / mothball owned factories, negotiate partner capacity & volume deals,
 * and ship finished goods to warehouses for online fulfilment.
 *
 * Every lever is staged in the decisions draft and previewed live through
 * buildWorkingGame (which replays the same intents the server applies at advance,
 * including cash charged for factory builds & expansions), so what's on screen is
 * exactly what resolves. Sub-sections are plain render functions (not nested
 * components) so React reconciles them in place and the page never scroll-jumps. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import { Engine, QUARTER_NAMES, fmtM, fmtK, fmt$ } from '@venturewrist/engine';
import { cityById, factoryCities, SEGMENT_BY_ID } from '../../lib/constants';
import { Flag } from '../../components/Flag';
import { useDecisions, factoryOpOf } from '../../store/decisions';
import { buildWorkingGame } from '../../lib/designState';
import {
  ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import type { ScreenProps } from './types';
import '../../styles/production.css';
import { useReduceMotion } from '../../hooks/useReduceMotion';

const Eng = Engine as any;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const scaleDisc = (q: number) => (q >= 200 ? 0.18 : q >= 100 ? 0.12 : q >= 50 ? 0.07 : q >= 20 ? 0.03 : 0);

/** A partner's response to a negotiation, shown in a result modal (the prototype's negWindow). */
type NegResult = {
  icon: string;
  tone: 'good' | 'warn' | 'bad';
  partnerName: string;
  relationship: number;
  quote: string;
  lines?: [string, string][];
};

const WhyChip = () => <span className="tooltip-why" title="Learn the theory">?</span>;
const Bar = ({ v, color }: { v: number; color?: string }) => (
  <div className="bar-track"><div className="bar-fill" style={{ width: clamp(v, 0, 100) + '%', background: color || undefined }} /></div>
);
const relClass = (rel: number) => (rel >= 0.95 ? 'good' : rel >= 0.9 ? 'warn' : 'bad');
const RelChip = ({ rel }: { rel: number }) => <span className={'chip ' + relClass(rel)}>{Math.round(rel * 100)}% reliable</span>;
const Stars = ({ n, max }: { n: number; max: number }) => (
  <span style={{ letterSpacing: 1, color: 'var(--acc2)' }}>{'★'.repeat(n)}<span style={{ opacity: 0.25 }}>{'★'.repeat(Math.max(0, max - n))}</span></span>
);
const SegTag = ({ segId }: { segId: string }) => {
  const s = SEGMENT_BY_ID[segId] as any; if (!s) return null;
  return <span className="seg-tag" style={{ background: s.color + '22', color: s.color }}>{s.icon} {s.name}</span>;
};
const warmth = (rel: number) => (rel > 70 ? 'trusted' : rel >= 45 ? 'warm' : 'cold');

export function Production({ detail }: ScreenProps) {
  const draft = useDecisions((z) => z.draft);
  const reduceMotion = useReduceMotion();
  const setProductGen = useDecisions((z) => z.setProductGen);
  const setProductAlloc = useDecisions((z) => z.setProductAlloc);
  const clearProductAlloc = useDecisions((z) => z.clearProductAlloc);
  const setAllocMode = useDecisions((z) => z.setAllocMode);
  const addFactoryBuild = useDecisions((z) => z.addFactoryBuild);
  const setFactoryOp = useDecisions((z) => z.setFactoryOp);
  const removeFactoryBuild = useDecisions((z) => z.removeFactoryBuild);
  const addContract = useDecisions((z) => z.addContract);
  const removeContract = useDecisions((z) => z.removeContract);
  const toggleExpansion = useDecisions((z) => z.toggleExpansion);
  const setWhShip = useDecisions((z) => z.setWhShip);
  const setWhAuto = useDecisions((z) => z.setWhAuto);
  const setGenOp = useDecisions((z) => z.setGenOp);

  const [builderOpen, setBuilderOpen] = useState(false);
  const [pb, setPb] = useState<{ city: string; cap: number }>({ city: factoryCities()[0]!.id, cap: 150 });
  const [negotiateId, setNegotiateId] = useState<string | null>(null);
  const [ng, setNg] = useState<{ qty: number; dur: number }>({ qty: 120, dur: 6 });
  const [negResult, setNegResult] = useState<NegResult | null>(null);

  const wg = useMemo(() => buildWorkingGame(detail.state, draft), [detail.state, draft]);
  Eng.ensureMfgNet(wg); Eng.ensureLogistics(wg);

  const srcs = Eng.prodSources(wg);
  const netCap = srcs.reduce((a: number, s: any) => a + s.capacity, 0);
  const netCapK = Math.max(500, Math.ceil(netCap));
  const models = Eng.activeModels(wg);
  const planned = models.reduce((a: number, m: any) => a + Eng.prodGens(m).reduce((x: number, gv: any) => x + gv.production, 0), 0);
  const last = wg.history.length ? wg.history[wg.history.length - 1] : null;
  const allocMode = wg.allocMode === 'rel' ? 'rel' : 'cost';

  // resolved per-source allocation for a generation (manual draft, else optimal preview)
  const genAllocView = (gv: any): Record<string, number> => {
    if (gv.alloc && Object.keys(gv.alloc).length) {
      const a: Record<string, number> = {};
      for (const s of srcs) if (gv.alloc[s.id]) a[s.id] = gv.alloc[s.id];
      return a;
    }
    const freeCap: Record<string, number> = {};
    for (const s of srcs) freeCap[s.id] = s.capacity;
    return Eng.autoAllocate(gv.spec, wg, gv.production, freeCap, allocMode).alloc;
  };

  const autoAllocAll = () => { for (const m of models) for (const gv of Eng.prodGens(m)) clearProductAlloc(m.id, gv.gen); };

  // ---- negotiation outcomes (stage the intent, then surface the partner's reply) ----
  const requestExpansion = (o: any) => {
    if (o.relationship > 70) {
      if (!draft.expansions?.includes(o.id)) toggleExpansion(o.id);
      const d = Math.round(o.capacity * 0.15);
      setNegResult({
        icon: '✅', tone: 'good', partnerName: o.name, relationship: o.relationship,
        quote: "Of course — you've been a reliable partner. We'll bring the new line online right away.",
        lines: [['Capacity added', '+' + fmtK(d * 1000) + '/qtr'], ['New total capacity', fmtK((o.capacity + d) * 1000) + '/qtr'], ['Cost to you', 'Free — on your standing']],
      });
      setNegotiateId(null);
    } else if (o.relationship >= 45) {
      setNegResult({
        icon: '🤔', tone: 'warn', partnerName: o.name, relationship: o.relationship,
        quote: "We can expand for you — but a line costs us real money. Commit to a volume below and we'll invest behind it.",
      });
    } else {
      setNegResult({
        icon: '🙅', tone: 'bad', partnerName: o.name, relationship: o.relationship,
        quote: "Your recent orders don't yet justify new investment. Order more consistently, or commit to volume, and we'll talk.",
      });
    }
  };

  const proposeDeal = (o: any) => {
    const discount = clamp((0.02 + ng.dur * 0.005 + ng.qty / 120 * 0.01) * (0.6 + o.relationship / 100), 0.02, 0.08);
    const capAdd = Math.round(ng.qty * 1.4);
    if ((draft.contracts?.length ?? 0) >= 20) {
      setNegResult({
        icon: '✋', tone: 'warn', partnerName: o.name, relationship: o.relationship,
        quote: 'You have already staged 20 deals this quarter — the most the round can carry. Submit the quarter before proposing more.',
      });
      return;
    }
    if (o.relationship < 45 && ng.dur < 6) {
      setNegResult({
        icon: '✋', tone: 'warn', partnerName: o.name, relationship: o.relationship,
        quote: "Given where our relationship stands, we'll only sign for at least a 6-quarter term. Lengthen the commitment and we have a deal.",
      });
      return; // keep the negotiation modal open so they can adjust
    }
    addContract({ partnerId: o.id, qty: ng.qty, dur: ng.dur });
    setNegResult({
      icon: '🤝', tone: 'good', partnerName: o.name, relationship: o.relationship,
      quote: "Done. We'll reserve the capacity and lock in your discounted rate for the full term. Hold up your end and this pays off for both of us.",
      lines: [
        ['Your commitment', ng.qty + 'k/qtr × ' + ng.dur + ' quarters'],
        ['Unit-cost discount', '−' + Math.round(discount * 100) + '%'],
        ['Reserved capacity', '+' + fmtK(capAdd * 1000) + '/qtr'],
        ['Take-or-pay clause', '40% of any quarter below commit'],
      ],
    });
    setNegotiateId(null);
  };

  // ---- allocation strip (sources for one producing generation) ----
  const allocStrip = (m: any, gv: any) => {
    const alloc = genAllocView(gv);
    const sum = Object.values(alloc).reduce((a, b) => a + (b || 0), 0);
    const blended = Eng.blendedCost(gv.spec, wg, alloc);
    const ok = Math.abs(sum - gv.production) < 0.5;
    return (
      <div className="alloc-strip" data-tut="production-alloc">
        <div className="spread" style={{ marginBottom: 4 }}>
          <span className="sub">Allocation across sources</span>
          <button className="btn btn-ghost btn-sm" onClick={() => clearProductAlloc(m.id, gv.gen)}>⚡ optimal</button>
        </div>
        {srcs.map((src: any) => {
          const a = Math.round(alloc[src.id] || 0);
          const uc = Eng.sourceUnitCost(src, gv.spec, wg, Math.max(1, a || gv.production));
          const capR = Math.max(1, Math.round(src.capacity));
          const pct = Math.min(100, (a / capR) * 100);
          return (
            <div key={src.id} className="row" style={{ gap: 8, alignItems: 'center', fontSize: 12, margin: '2px 0' }}>
              <span style={{ width: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {src.kind === 'factory' ? '🏭 ' : '🤝 '}
                {src.kind === 'factory' ? <Flag cc={cityById(src.city)?.cc} country={cityById(src.city)?.country} /> : <Flag country={src.country} />}
                {src.name}
              </span>
              <span className="sub" style={{ width: 54, textAlign: 'right' }}>{fmt$(uc)}</span>
              <RelChip rel={src.rel} />
              <input type="range" className="rng" style={{ flex: 1, ['--p' as any]: pct + '%' }} min={0} max={capR} step={5} value={a}
                onChange={(e) => setProductAlloc(m.id, gv.gen, src.id, Number(e.target.value), alloc)} />
              <b style={{ width: 42, textAlign: 'right' }}>{a}k</b>
            </div>
          );
        })}
        <div className="spread" style={{ marginTop: 4 }}>
          <span className="sub">Blended unit cost: <b style={{ color: 'var(--txt)' }}>{fmt$(blended)}</b></span>
          <span className="sub" style={{ color: ok ? 'var(--good)' : 'var(--warn)' }}>allocated {Math.round(sum)}k / {gv.production}k {ok ? '✓' : '(auto-balances at quarter end)'}</span>
        </div>
      </div>
    );
  };

  // ---- one model's production card ----
  const prodCard = (m: any) => {
    const live = Eng.liveGens(m).slice().sort((a: any, b: any) => b.gen - a.gen);
    const devG = Eng.devGen(m);
    const multi = live.length > 1 || m.gens.length > 1;
    const combined = Eng.prodGens(m).reduce((a: number, x: any) => a + x.production, 0);
    const missedDisc = Eng.prodGens(m).length > 1 && scaleDisc(combined) > Math.max(...Eng.prodGens(m).map((x: any) => scaleDisc(x.production)));
    return (
      <div className="card" key={m.id}>
        <div className="spread" style={{ marginBottom: 6 }}>
          <div className="row"><b>{m.name}</b></div>
          <SegTag segId={m.targetSeg} />
        </div>
        {live.map((gv: any) => {
          const st = Eng.genStatus(m, gv);
          const S = Eng.STATUS[st];
          const lr = last && last.products ? last.products.find((r: any) => r.mid === m.id && r.gen === gv.gen) : null;
          if (st === 'sellthrough') {
            return (
              <div className="gen-row dim" key={gv.gen}>
                <span className="gen-chip" style={{ color: S.color }}>{(multi ? 'Gen ' + gv.gen + ' ' : '') + S.icon}</span>
                <span className="sub" style={{ flex: 1 }}>production 0 — selling through {fmtK(gv.inventory)} inventory at {fmt$(gv.price)}</span>
                {!gv.wound && <button className="btn btn-ghost btn-sm" onClick={() => setGenOp(m.id, gv.gen, 'resume')}>↺ resume</button>}
                <button className="btn btn-danger btn-sm" onClick={() => { if (window.confirm('Write off ' + fmtK(gv.inventory) + ' units of ' + Eng.genName(m, gv) + ' for 15% salvage? The generation retires immediately.')) setGenOp(m.id, gv.gen, 'writeoff'); }}>✕ write off</button>
              </div>
            );
          }
          const uc = Eng.unitCost(gv.spec, wg, gv.production);
          const pPct = Math.min(100, (gv.production / Math.max(1, netCapK)) * 100);
          return (
            <div key={gv.gen}>
              <div className="gen-row" data-tut="production-volume">
                {multi && <span className="gen-chip" style={{ color: S.color }}>Gen {gv.gen}</span>}
                <input type="range" className="rng" style={{ flex: 1, ['--p' as any]: pPct + '%' }} min={0} max={netCapK} step={5} value={gv.production}
                  onChange={(e) => setProductGen(m.id, gv.gen, { production: Number(e.target.value) })} />
                <b style={{ width: 46, textAlign: 'right' }}>{gv.production}k</b>
                <span className="sub" style={{ textAlign: 'right', minWidth: 190 }}>
                  best unit {fmt$(uc)} · inv {fmtK(gv.inventory)}
                  {lr && <><br />last qtr: demand {fmtK(lr.demand)} · sold {fmtK(lr.sold)}{lr.directOnline > 0 && <> (of which {fmtK(lr.directOnline)} shipped direct)</>}{lr.stockout > 0 && <> · <span className="neg">stockout {Math.round(lr.stockout * 100)}%</span></>}</>}
                </span>
              </div>
              {gv.production > 0 && allocStrip(m, gv)}
            </div>
          );
        })}
        {devG && (
          <div className="gen-row dim">
            <span className="gen-chip" style={{ color: 'var(--warn)' }}>🔧 Gen {devG.gen}</span>
            <span className="sub">launches {QUARTER_NAMES(wg.round + 1)} — inherits the current gen's production &amp; price</span>
          </div>
        )}
        {missedDisc && <div className="sub" style={{ color: 'var(--warn)', marginTop: 8 }}>⚠ {combined}k combined would earn the {Math.round(scaleDisc(combined) * 100)}% scale discount, but discounts apply per generation.</div>}
        {!live.length && <div className="sub">No sellable generations.</div>}
        {(draft.genOps ?? []).filter((op) => op.productId === m.id).map((op) => (
          <div className="sub" key={op.gen} style={{ marginTop: 6 }}>
            Staged: {op.op === 'writeoff' ? 'write off' : 'resume'} Gen {op.gen}
            <span style={{ cursor: 'pointer', color: 'var(--bad)', marginLeft: 8 }} title="Unstage" onClick={() => setGenOp(m.id, op.gen, null)}>✕ undo</span>
          </div>
        ))}
      </div>
    );
  };

  // ---- cost-index meter (lower = cheaper) ----
  const costMeter = (label: string, idx: number) => {
    const lvl = idx <= 0.9 ? { t: 'Low', c: 'var(--good)' } : idx <= 1.15 ? { t: 'Medium', c: 'var(--warn)' } : { t: 'High', c: 'var(--bad)' };
    const pct = clamp(Math.round(((idx - 0.7) / (1.8 - 0.7)) * 100), 6, 100);
    return (
      <div style={{ margin: '6px 0' }}>
        <div className="spread" style={{ fontSize: 11, marginBottom: 3 }}><span className="sub">{label}</span><b style={{ color: lvl.c }}>{lvl.t} <span className="sub" style={{ fontWeight: 400 }}>(×{idx.toFixed(2)})</span></b></div>
        <div className="bar-track" style={{ height: 7 }}><div className="bar-fill" style={{ width: pct + '%', background: lvl.c }} /></div>
      </div>
    );
  };

  // ---- factory builder ----
  const factoryBuilderPanel = () => {
    const cities = factoryCities();
    const sc = cityById(pb.city) || cities[0]!;
    const setup = Eng.setupCost(pb.city, pb.cap), fixed = Eng.fixedCost(pb.city, pb.cap);
    const q = Eng.factoryBuildQuarters(pb.cap);
    const sampleSpec = { cpu: 'c2', display: 'd2', battery: 'b2', casing: 'k2', strap: 's1', size: '44', style: 'modern', color: 'midnight', sensors: ['hr'] };
    const fakeFactory = { city: pb.city, capacity: pb.cap, builtRound: wg.round };
    const ucFull = Eng.factoryUnitCost(sampleSpec, wg, fakeFactory, pb.cap);
    const fox = Eng.outsourcerById(wg, 'cna'); const foxUC = fox ? Eng.outsourcerUnitCost(sampleSpec, wg, fox, pb.cap) : ucFull;
    const allIn = ucFull + fixed / (pb.cap * 1000);
    return (
      <div className="alloc-strip" style={{ marginTop: 10, padding: 14 }}>
        <div className="sub" style={{ marginBottom: 10, lineHeight: 1.5 }}>
          <b style={{ color: 'var(--txt)' }}>How to read these cities.</b> <span style={{ color: 'var(--acc2)' }}>★ Manufacturing fit</span> rates the local ecosystem (nearby suppliers, skilled assembly, ramp speed). It is <b>separate from cost</b> — cost comes from the labor &amp; construction indices below.
        </div>
        <div className="sub" style={{ marginBottom: 6 }}>Choose a city:</div>
        <div className="row" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {cities.map((c) => (
            <span key={c.id} className={'opt-pill ' + (pb.city === c.id ? 'on' : '')} title={c.note} onClick={() => setPb((p) => ({ ...p, city: c.id }))}>
              <Flag cc={c.cc} country={c.country} /> {c.city} <Stars n={c.factoryFit} max={3} />
            </span>
          ))}
        </div>
        <div className="card" style={{ padding: 12, marginBottom: 12, background: 'var(--field)' }}>
          <div className="spread" style={{ marginBottom: 4 }}>
            <b><Flag cc={sc.cc} country={sc.country} /> {sc.city} <span className="sub" style={{ fontWeight: 400 }}>· {sc.country}</span></b>
            <span className="sub">Manufacturing fit <Stars n={sc.factoryFit} max={3} /></span>
          </div>
          <div className="sub" style={{ marginBottom: 6 }}>{sc.note}</div>
          <div className="grid2" style={{ gap: 14 }}>{costMeter('Labor cost (drives unit cost)', sc.laborIdx)}{costMeter('Construction cost (drives setup & fixed)', sc.buildIdx)}</div>
        </div>
        <div className="spread"><span className="sub">Capacity: <b style={{ color: 'var(--txt)' }}>{pb.cap}k/qtr</b></span><span className="chip warn">🏗️ Build time: {q} quarter{q > 1 ? 's' : ''}</span></div>
        <input type="range" min={50} max={500} step={25} value={pb.cap} style={{ width: '100%' }} onChange={(e) => setPb((p) => ({ ...p, cap: Number(e.target.value) }))} />
        <div className="sub" style={{ marginTop: 2, fontSize: 10.5 }}>Bigger factories take longer to build (50–150k: 1 qtr · up to 300k: 2 · up to 450k: 3 · 500k: 4).</div>
        <div className="grid3" style={{ margin: '12px 0' }}>
          <div><div className="sub">Setup cost</div><b>{fmtM(setup)}</b></div>
          <div><div className="sub">Fixed/qtr (once open)</div><b>{fmtM(fixed)}</b></div>
          <div><div className="sub">Unit @ scale</div><b>{fmt$(ucFull)}</b></div>
        </div>
        <div className="sub" style={{ marginBottom: 8 }}>At full utilization all-in ≈ {fmt$(allIn)}/unit. FoxLine ≈ {fmt$(foxUC)}/unit.</div>
        <div className="row">
          <button className="btn btn-pri" disabled={wg.cash < setup} onClick={() => { addFactoryBuild({ city: pb.city, cap: pb.cap }); setBuilderOpen(false); }}>Confirm — build for {fmtM(setup)} · ready in {q} qtr{q > 1 ? 's' : ''}</button>
          <button className="btn btn-ghost" onClick={() => setBuilderOpen(false)}>Cancel</button>
        </div>
        {wg.cash < setup && <div className="sub" style={{ color: 'var(--bad)', marginTop: 6 }}>Insufficient cash.</div>}
      </div>
    );
  };

  // ---- one owned factory card ----
  const factoryCard = (p: any) => {
    const city = cityById(p.city) || { city: p.city, country: '', cc: undefined, laborIdx: 1 } as any;
    const src = srcs.find((s: any) => s.id === p.id);
    const moth = p.status === 'mothballed';
    const building = p.status === 'building';
    const title = <b><Flag cc={city.cc} country={city.country} /> {city.city}{moth ? ' (mothballed)' : ''}</b>;
    if (building) {
      const left = Math.max(1, (p.readyRound || wg.round) - wg.round);
      // A build staged this quarter (id ends in its index in the draft) can
      // still be unstaged; once submitted the setup cost is sunk.
      const stagedIdx = p.orderedRound === wg.round ? Number(String(p.id).split('-').pop()) : NaN;
      const staged = Number.isInteger(stagedIdx) && !!draft.factoryBuilds?.[stagedIdx];
      return (
        <div className="card" key={p.id} style={{ borderColor: 'rgb(var(--c-warn) / .45)', background: 'rgb(var(--c-warn) / .05)' }}>
          <div className="spread"><b><Flag cc={city.cc} country={city.country} /> {city.city}</b><span className="chip warn">{staged ? '🏗️ Staged' : '🏗️ Building'}</span></div>
          <div className="sub" style={{ marginTop: 4 }}>Capacity {p.capacity}k/qtr · {staged ? 'setup charged at submit' : 'setup paid'} · no fixed cost or output until it opens.</div>
          <div className="sub" style={{ marginTop: 6, color: 'var(--warn)' }}>Ready {QUARTER_NAMES(p.readyRound)} — {left} quarter{left > 1 ? 's' : ''} to go, then ramps over ~3 quarters.</div>
          {staged && <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => removeFactoryBuild(stagedIdx)}>✕ cancel build</button>}
        </div>
      );
    }
    const expandCost = 16e3 * 50 * (city.buildIdx || 1);
    const stagedOp = factoryOpOf(draft, p.id);
    const uc = Eng.factoryUnitCost({ cpu: 'c2', display: 'd2', battery: 'b2', casing: 'k2', strap: 's1', size: '44', style: 'modern', color: 'midnight', sensors: ['hr'] }, wg, p, p.capacity);
    return (
      <div className="card" key={p.id} style={moth ? { opacity: 0.6 } : undefined}>
        <div className="spread">{title}<span className="row" style={{ gap: 5 }}>{!moth && <span className="chip good">✓ Built</span>}{src && <RelChip rel={src.rel} />}</span></div>
        <div className="sub" style={{ marginTop: 4 }}>Capacity {p.capacity}k/qtr · fixed {fmtM(Eng.fixedCost(p.city, p.capacity) * (moth ? 0.30 : 1))}/qtr · ~unit {fmt$(uc)}{p.ramp < 1 && !moth ? ' · ramping (' + Math.round(p.ramp * 100) + '%)' : ''}</div>
        <div className="row" style={{ marginTop: 8, gap: 6 }}>
          {moth
            ? <button className="btn btn-ghost btn-sm" onClick={() => setFactoryOp(p.id, stagedOp === 'mothball' ? null : 'reactivate')}>↺ reactivate</button>
            : stagedOp === 'expand'
              ? <button className="btn btn-ghost btn-sm" onClick={() => setFactoryOp(p.id, null)}>✕ undo expand (+50k staged, {fmtM(expandCost)})</button>
              : <>
                  <button className="btn btn-ghost btn-sm" disabled={wg.cash < expandCost} title={wg.cash < expandCost ? 'Insufficient cash to expand (' + fmtM(expandCost) + ')' : 'Adds 50k/qtr for ' + fmtM(expandCost)} onClick={() => setFactoryOp(p.id, 'expand')}>＋ expand 50k · {fmtM(expandCost)}</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setFactoryOp(p.id, 'mothball')}>⏸ mothball</button>
                </>}
          {wg.cash < expandCost && !moth && stagedOp !== 'expand' && <span className="sub" style={{ color: 'var(--bad)' }}>Insufficient cash to expand.</span>}
        </div>
      </div>
    );
  };

  // ---- manufacturing partner row ----
  const partnerRow = (o: any) => {
    const uc = Eng.outsourcerUnitCost({ cpu: 'c2', display: 'd2', battery: 'b2', casing: 'k2', strap: 's1', size: '44', style: 'modern', color: 'midnight', sensors: ['hr'] }, wg, o, 150);
    const disc = Eng.contractDiscount(wg, o.id);
    const rec = o.lastExpansion && o.lastExpansion.round > wg.round - 4;
    return (
      <div className="gen-row" key={o.id}>
        <b style={{ width: 150 }}>🤝 {o.name}</b>
        <span className="sub" style={{ width: 104 }}><Flag country={o.country} /> {o.country}</span>
        <RelChip rel={o.rel} />
        <span className="sub" style={{ flex: 1 }}>~unit {fmt$(uc)}{disc > 0 && <span style={{ color: 'var(--good)' }}> (−{Math.round(disc * 100)}% contract)</span>} · cap {fmtK(o.capacity * 1000)}/qtr · relationship {Math.round(o.relationship)}/100{rec && <span style={{ color: 'var(--good)' }}> · +{fmtK(o.lastExpansion.amount * 1000)} {QUARTER_NAMES(o.lastExpansion.round)}</span>}</span>
        <button className="btn btn-ghost btn-sm" onClick={() => { setNegResult(null); setNegotiateId(o.id); setNg({ qty: 120, dur: 6 }); }}>Negotiate</button>
      </div>
    );
  };

  // ---- active negotiated deals ----
  const dealsCard = () => {
    const cons = ((wg.mfgNet && wg.mfgNet.contracts) || []).filter((k: any) => k.quartersLeft > 0);
    if (!cons.length) return null;
    return (
      <div className="card" style={{ marginTop: 14, borderColor: 'rgb(var(--c-good) / .4)' }}>
        <div className="spread"><h3 style={{ margin: 0 }}>📑 Active manufacturing deals</h3><span className="chip good">{cons.length} active</span></div>
        <div className="sub" style={{ margin: '4px 0 8px' }}>Signed volume commitments — their discounted unit cost and reserved capacity apply automatically each quarter while active.</div>
        {cons.map((k: any, i: number) => {
          const o = Eng.outsourcerById(wg, k.partnerId) || { name: k.partnerId, country: '' };
          const capAdd = Math.round(k.qtyCommit * 1.4);
          // Proposed this quarter → still just a staged intent, can be withdrawn.
          const stagedIdx = k.startRound === wg.round
            ? (draft.contracts ?? []).findIndex((c) => c.partnerId === k.partnerId && c.qty === k.qtyCommit && c.dur === k.quartersLeft)
            : -1;
          return (
            <div className="gen-row" key={i} style={{ alignItems: 'center' }}>
              <b style={{ width: 170 }}>🤝 <Flag country={o.country} /> {o.name}</b>
              <span className="chip good" style={{ marginRight: 6 }}>−{Math.round(k.discount * 100)}% unit cost</span>
              <span className="sub" style={{ flex: 1 }}>Committed {k.qtyCommit}k/qtr · +{fmtK(capAdd * 1000)} reserved capacity · take-or-pay {Math.round(k.penaltyRate * 100)}% below commit</span>
              {stagedIdx >= 0
                ? <button className="btn btn-ghost btn-sm" onClick={() => removeContract(stagedIdx)}>✕ withdraw (staged)</button>
                : <span className={'chip ' + (k.quartersLeft <= 2 ? 'warn' : '')}>{k.quartersLeft} qtr{k.quartersLeft > 1 ? 's' : ''} left</span>}
            </div>
          );
        })}
      </div>
    );
  };

  // ---- negotiation modal (centered overlay; matches the prototype's dialog) ----
  const negotiateModal = () => {
    const o = Eng.outsourcerById(wg, negotiateId);
    if (!o) return null;
    const warm = warmth(o.relationship);
    const discount = clamp((0.02 + ng.dur * 0.005 + ng.qty / 120 * 0.01) * (0.6 + o.relationship / 100), 0.02, 0.08);
    const capAdd = Math.round(ng.qty * 1.4);
    const reqStaged = !!draft.expansions?.includes(o.id);
    return (
      <div className="pr-modal-bg" onClick={() => setNegotiateId(null)}>
        <div className="card pr-modal" onClick={(e) => e.stopPropagation()}>
          <div className="spread"><h3 style={{ margin: 0 }}>Negotiate with {o.name}</h3><span className={'chip ' + (warm === 'trusted' ? 'good' : warm === 'warm' ? 'warn' : 'bad')}>relationship {Math.round(o.relationship)}/100 ({warm})</span></div>
          <div className="sub" style={{ margin: '8px 0' }}>Leverage comes from being a good, predictable customer. {o.relationship > 70 ? "They're ready to expand on request." : o.relationship >= 45 ? "They'll expand — with a volume commitment." : "Your recent orders don't justify investment yet — commit volume or order more consistently."}</div>
          <div className="row" style={{ gap: 8, marginBottom: 10 }}>
            <button className={'btn btn-sm ' + (reqStaged ? 'btn-pri' : 'btn-ghost')} onClick={() => requestExpansion(o)}>{reqStaged ? '✓ Expansion requested' : 'Request expansion now'}</button>
          </div>
          <div className="alloc-strip" style={{ padding: 12 }}>
            <b>Commit to volume</b>
            <div className="sub" style={{ marginTop: 6 }}>Quantity: <b style={{ color: 'var(--txt)' }}>{ng.qty}k/qtr</b></div>
            <input type="range" min={40} max={400} step={20} value={ng.qty} style={{ width: '100%' }} onChange={(e) => setNg((x) => ({ ...x, qty: Number(e.target.value) }))} />
            <div className="sub">Duration: <b style={{ color: 'var(--txt)' }}>{ng.dur} quarters</b></div>
            <input type="range" min={2} max={12} step={1} value={ng.dur} style={{ width: '100%' }} onChange={(e) => setNg((x) => ({ ...x, dur: Number(e.target.value) }))} />
            <div className="sub" style={{ margin: '8px 0' }}>They offer: <b style={{ color: 'var(--good)' }}>+{fmtK(capAdd * 1000)} capacity reserved · −{Math.round(discount * 100)}% unit cost</b>. You accept a take-or-pay clause: pay 40% of any quarter you order below {ng.qty}k.</div>
            <button className="btn btn-pri" onClick={() => proposeDeal(o)}>Propose deal</button>{' '}
            <button className="btn btn-ghost" onClick={() => setNegotiateId(null)}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  // ---- negotiation result window (the prototype's negWindow) ----
  const renderNegResult = () => {
    if (!negResult) return null;
    const warm = warmth(negResult.relationship);
    return (
      <div className="pr-modal-bg" onClick={() => setNegResult(null)}>
        <div className="card pr-modal" style={{ width: 'min(440px,92vw)', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
          <div style={{ fontSize: 42, lineHeight: 1, marginBottom: 8 }}>{negResult.icon}</div>
          <div className="spread" style={{ justifyContent: 'center', gap: 8, marginBottom: 10 }}>
            <b style={{ fontSize: 15 }}>{negResult.partnerName}</b>
            <span className={'chip ' + (warm === 'trusted' ? 'good' : warm === 'warm' ? 'warn' : 'bad')}>relationship {Math.round(negResult.relationship)}/100 · {warm}</span>
          </div>
          <p style={{ fontStyle: 'italic', color: 'var(--txt)', maxWidth: 380, margin: '0 auto 14px', lineHeight: 1.55 }}>“{negResult.quote}”</p>
          {negResult.lines && (
            <div className="card" style={{ maxWidth: 360, margin: '0 auto 14px', textAlign: 'left' }}>
              {negResult.lines.map(([k, v], i) => (
                <div className="spread" key={i}><span className="sub">{k}</span><b>{v}</b></div>
              ))}
            </div>
          )}
          <button className="btn btn-pri" onClick={() => setNegResult(null)}>Got it</button>
        </div>
      </div>
    );
  };

  // ---- warehouse shipment panel ----
  const whShipPanel = () => {
    const whs = Eng.readyWarehouses(wg);
    const building = (wg.warehouses || []).filter((w: any) => !Eng.whReady(w, wg));
    const sup = Eng.whSupply(wg);
    const auto = wg.whAlloc == null;
    const skus = Eng.liveSkus(wg);
    const whHeldTot = whs.reduce((a: number, w: any) => a + Eng.whStockTotal(w), 0);
    const factoryInv = Math.max(0, skus.reduce((a: number, sk: any) => a + (sk.gv.inventory || 0), 0) - whHeldTot);
    const plannedUnits = models.reduce((a: number, m: any) => a + Eng.prodGens(m).reduce((x: number, gv: any) => x + gv.production, 0), 0) * 1000;
    const availToShip = factoryInv + plannedUnits;
    if (!whs.length) {
      return (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="spread" style={{ flexWrap: 'wrap', gap: 8 }}>
            <div><b>📦 Ship production to warehouses</b><div className="sub">Online orders ship only from warehouse stock. Store, distributor &amp; carrier orders ship direct from factories.</div></div>
          </div>
          <div className="sub" style={{ marginTop: 10, color: 'var(--bad)' }}>⚠ No operating warehouses — every online order will stock out. Build one on the Logistics screen to fulfil online demand.</div>
          {!!building.length && <div className="sub" style={{ marginTop: 6, color: 'var(--warn)' }}>🏗️ {building.map((w: any) => (cityById(w.city) || { city: w.city }).city + ' (ready ' + QUARTER_NAMES(w.readyRound) + ')').join(', ')} still under construction.</div>}
        </div>
      );
    }
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="spread" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div><b>📦 Ship production to warehouses</b><div className="sub">Online orders ship <b>only</b> from warehouse stock — allocate how much finished product goes to each warehouse each quarter. Unsold warehouse stock carries over.{auto && <span className="sub"> · auto</span>}</div></div>
          <button className="btn btn-pri btn-sm" onClick={() => setWhAuto()}>⚡ Auto-allocate</button>
        </div>
        <div className="sub" style={{ marginTop: 4 }}>Allocated to warehouses: <b style={{ color: 'var(--txt)' }}>{fmtK(sup.total)}/qtr</b>. Available to ship this quarter: <b style={{ color: 'var(--txt)' }}>{fmtK(availToShip)}</b> ({fmtK(factoryInv)} on hand + {fmtK(plannedUnits)} planned output).</div>
        <div className="grid2" style={{ marginTop: 12 }}>
          {whs.map((w: any) => {
            const city = cityById(w.city) || { city: w.city, cc: undefined } as any;
            const v = sup.byWh[w.id] || 0; const kv = Math.round(v / 1000); const capK = Math.round(w.capacity / 1000);
            const stock = Eng.whStockTotal(w); const stockPct = w.capacity > 0 ? Math.min(100, (stock / w.capacity) * 100) : 0;
            const full = stock >= w.capacity * 0.999;
            return (
              <div className="alloc-strip" key={w.id} style={{ padding: '10px 12px' }}>
                <div className="spread"><b><Flag cc={city.cc} country={city.country} /> {city.city}</b><span className="sub">cap {fmtK(w.capacity)}/qtr</span></div>
                <div className="spread" style={{ marginTop: 6, alignItems: 'center' }}><span className="sub">📊 In stock</span><b style={{ color: stock > 0 ? 'var(--good)' : 'var(--mut)' }}>{fmtK(stock)}</b></div>
                <div className="bar-track" style={{ height: 6, marginTop: 3 }}><div className="bar-fill" style={{ width: stockPct + '%', background: full ? 'var(--warn)' : 'var(--good)' }} /></div>
                <div className="spread" style={{ marginTop: 8, alignItems: 'center', gap: 8 }}><span className="sub">Ship/qtr</span>
                  <input type="number" min={0} max={capK} step={10} value={kv} style={{ width: 90 }} onChange={(e) => setWhShip(w.id, Number(e.target.value))} /><span className="sub">k units</span></div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ---- network capacity chart (recharts; stacked capacity bands + used line) ----
  const chartData = (wg.history || []).map((r: any) => ({
    label: r.label, own: r.capSnap?.own || 0, foxline: r.capSnap?.foxline || 0, other: r.capSnap?.other || 0, used: r.capSnap?.used || 0,
  }));

  const recents = ((wg.mfgNet && wg.mfgNet.outsourcers) || []).filter((o: any) => o.lastExpansion && o.lastExpansion.round > wg.round - 4);
  const factories = wg.factories || [];
  const activeFacCount = Eng.activeFactories(wg).length;
  const overCap = planned > netCap;

  return (
    <div className="pd-root">
      <div className="screen-head">
        <h2>🏭 Production &amp; Manufacturing Network<WhyChip /></h2>
        <div className="sub">Build factories, allocate each quarter's orders across your factories and outsource partners, and negotiate capacity. Owning capacity trades a fixed cost for a lower marginal cost.</div>
      </div>

      {!!recents.length && (
        <div className="freeze-banner" style={{ borderColor: 'rgb(var(--c-good) / .45)', color: 'var(--good)', marginBottom: 12 }}>
          📈 Recent capacity change: {recents.map((o: any) => o.name + ' +' + fmtK(o.lastExpansion.amount * 1000) + ' (' + QUARTER_NAMES(o.lastExpansion.round) + ')').join(' · ')}
        </div>
      )}

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="spread">
          <div><b>Network capacity: {planned}k planned / {Math.round(netCap)}k available</b>
            <div className="sub">{activeFacCount} factor{activeFacCount === 1 ? 'y' : 'ies'} + {wg.mfgNet.outsourcers.length} partners</div></div>
          <span className={'chip ' + (overCap ? 'bad' : 'good')}>{netCap > 0 ? Math.round((planned / netCap) * 100) : 0}% utilization</span>
        </div>
        <div style={{ marginTop: 10 }}><Bar v={netCap > 0 ? (planned / netCap) * 100 : 0} color={overCap ? 'var(--bad)' : undefined} /></div>
        {overCap && <div className="sub" style={{ color: 'var(--bad)', marginTop: 6 }}>Over capacity — orders will be scaled down proportionally.</div>}
        {chartData.length > 0 && (
          <>
            <div style={{ height: 220, marginTop: 14 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ left: 4, right: 8, top: 4 }}>
                  <CartesianGrid stroke="rgb(var(--c-txt) / .08)" vertical={false} />
                  <XAxis dataKey="label" stroke="var(--mut)" fontSize={11} />
                  <YAxis stroke="var(--mut)" fontSize={11} />
                  <Tooltip contentStyle={{ background: 'var(--bg2)', border: '1px solid var(--field-b)', borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="own" name="Own factories" stackId="cap" stroke="var(--vio)" fill="rgb(var(--c-vio) / .35)" strokeWidth={1} isAnimationActive={!reduceMotion} />
                  <Area type="monotone" dataKey="foxline" name="FoxLine" stackId="cap" stroke="var(--acc)" fill="rgb(var(--c-acc) / .30)" strokeWidth={1} isAnimationActive={!reduceMotion} />
                  <Area type="monotone" dataKey="other" name="Other partners" stackId="cap" stroke="var(--good)" fill="rgb(var(--c-good) / .28)" strokeWidth={1} isAnimationActive={!reduceMotion} />
                  <Line type="monotone" dataKey="used" name="Used" stroke="var(--warn)" strokeWidth={3} dot={false} isAnimationActive={!reduceMotion} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="sub" style={{ marginTop: 4 }}>Stacked bands = capacity by source; the solid line = units used.</div>
          </>
        )}
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="spread" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div><b>Order allocation</b><div className="sub">Each producing generation is routed across sources. Auto-allocate solves it for you.</div></div>
          <div className="row">
            <span className={'opt-pill ' + (allocMode === 'cost' ? 'on' : '')} onClick={() => setAllocMode('cost')}>⚡ Lowest cost</span>
            <span className={'opt-pill ' + (allocMode === 'rel' ? 'on' : '')} onClick={() => setAllocMode('rel')}>🛡️ Balance reliability</span>
            <button className="btn btn-pri btn-sm" onClick={autoAllocAll}>⚡ Auto-allocate all</button>
          </div>
        </div>
      </div>

      {whShipPanel()}

      {models.length ? <div className="grid2">{models.map(prodCard)}</div> : <div className="card"><div className="sub">No active products. Design one on the Design Studio screen first.</div></div>}

      <div className="card" style={{ marginTop: 14 }}>
        <div className="spread"><h3 style={{ margin: 0 }}>🏭 Your factories</h3><button className="btn btn-pri btn-sm" onClick={() => setBuilderOpen((v) => !v)}>＋ Build a factory</button></div>
        {builderOpen && factoryBuilderPanel()}
        {!factories.length && <div className="sub" style={{ marginTop: 8 }}>No owned factories — you rely entirely on outsource partners. A well-utilized factory beats them on unit cost.</div>}
        <div className="grid2" style={{ marginTop: 10 }}>{factories.map(factoryCard)}</div>
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <h3>🤝 Manufacturing partners</h3>
        {wg.mfgNet.outsourcers.map(partnerRow)}
      </div>

      {dealsCard()}

      <div className="card" style={{ marginTop: 14 }}>
        <h3>Scale discounts</h3>
        <div className="row" style={{ gap: 18, flexWrap: 'wrap' }}>
          {[['<20k', '0%'], ['20k+', '3%'], ['50k+', '7%'], ['100k+', '12%'], ['200k+', '18%']].map(([a, b]) => <span className="chip" key={a}>{a} → −{b}</span>)}
          <span className="sub">applied per generation per quarter</span>
        </div>
      </div>

      {negResult ? renderNegResult() : negotiateId ? negotiateModal() : null}
    </div>
  );
}
